import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const TEAM = [
	{
		name: 'Robert Aguilar',
		title: 'Owner & General Contractor',
		bio: 'Robert has worked in residential construction and remodeling for over 18 years. He founded A-Z Home Improvement LLC in 2019 to provide the Phoenix area with a reliable, quality-first alternative to the large national chains. He personally oversees every project.',
	},
	{
		name: 'Sandra Whitfield',
		title: 'Project Manager',
		bio: 'Sandra brings 11 years of project coordination experience in the construction industry. She is the primary point of contact for every A-Z client — managing timelines, subcontractor schedules, and client communications from kickoff to completion.',
	},
	{
		name: 'Carlos Mendez',
		title: 'Lead Estimator',
		bio: 'Carlos has been preparing accurate construction estimates for over 14 years. His detailed, transparent pricing models are the reason our clients always know exactly what they\'re getting and what it will cost — before a single tool is lifted.',
	},
	{
		name: 'Biswanath Sahoo',
		title: 'IT Consultant & Technical Advisor',
		bio: 'Biswanath handles the company\'s digital infrastructure, CRM systems, client communications platform, and website operations — ensuring our team can focus on building while clients stay informed at every stage.',
	},
];

const VALUES = [
	{ icon: '🏆', title: 'Quality First', desc: 'We don\'t cut corners. Every project is completed to the highest standard, regardless of size or budget.' },
	{ icon: '📋', title: 'Transparency', desc: 'Written estimates. Honest timelines. No hidden fees. You know exactly what you\'re paying for and why.' },
	{ icon: '🤝', title: 'Accountability', desc: 'We own every outcome. If something isn\'t right, we fix it — that\'s what our 5-year warranty is for.' },
	{ icon: '🌵', title: 'Local Commitment', desc: 'We are a Phoenix-area business through and through. We live here, work here, and care about this community.' },
];

function About() {
	return (
		<>
			<Helmet>
				<title>About Us | A-Z Home Improvement LLC | Phoenix, AZ</title>
				<meta name="description" content="Learn about A-Z Home Improvement LLC — Phoenix's trusted licensed home remodeling contractor since 2019. Meet our team and learn our story." />
			</Helmet>
			<PageHeader
				title="About A-Z Home Improvement LLC"
				subtitle="A Phoenix-based remodeling company built on quality, transparency, and community."
			/>

			{/* Story */}
			<section className="section">
				<div className="container two-col" style={{ gap: 40, alignItems: 'center' }}>
					<div>
						<h2>Our Story</h2>
						<p>A-Z Home Improvement LLC was founded in Phoenix, Arizona in 2019 by Robert Aguilar — a third-generation general contractor with nearly two decades of hands-on experience in residential construction and renovation.</p>
						<p>Robert started A-Z after seeing too many homeowners get burned by contractors who overpromised, underdelivered, and disappeared when problems arose. His goal was simple: build a remodeling company that operates the way a company <em>should</em> — with honest pricing, consistent communication, and craftsmanship you can be proud of.</p>
						<p>Today, A-Z Home Improvement serves homeowners across the Phoenix metro area, from kitchen and bathroom renovations to full-home remodels, decks, flooring, painting, and more. We are fully licensed under the Arizona Registrar of Contractors (ROC License #XXXXX8142), bonded, and insured with $2 million in general liability coverage.</p>
					</div>
					<div>
						<img
							src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1200&auto=format&fit=crop"
							alt="Home remodeling team at work in Phoenix, AZ"
							style={{ width: '100%', borderRadius: 10, display: 'block' }}
						/>
					</div>
				</div>
			</section>

			{/* Values */}
			<section className="section section-alt">
				<div className="container">
					<div style={{ textAlign: 'center', marginBottom: 28 }}>
						<h2>What We Stand For</h2>
						<p className="helper" style={{ maxWidth: 480, margin: '0 auto' }}>Four principles guide every project we take on.</p>
					</div>
					<div className="grid">
						{VALUES.map(v => (
							<div key={v.title} className="card feature">
								<div style={{ fontSize: 32, marginBottom: 10 }}>{v.icon}</div>
								<h3 style={{ margin: '0 0 6px' }}>{v.title}</h3>
								<p className="helper" style={{ margin: 0 }}>{v.desc}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Team */}
			<section className="section section-light">
				<div className="container">
					<div style={{ textAlign: 'center', marginBottom: 28 }}>
						<h2>Meet the Team</h2>
						<p className="helper" style={{ maxWidth: 480, margin: '0 auto' }}>Experienced professionals who care about your home as much as you do.</p>
					</div>
					<div className="grid">
						{TEAM.map(m => (
							<div key={m.name} className="card" style={{ padding: '24px 20px' }}>
								<div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 22, color: '#fff', marginBottom: 12 }}>
									{m.name.charAt(0)}
								</div>
								<h3 style={{ margin: '0 0 2px', fontSize: 17 }}>{m.name}</h3>
								<p style={{ color: 'var(--brand)', fontWeight: 600, fontSize: 13, margin: '0 0 10px' }}>{m.title}</p>
								<p className="helper" style={{ margin: 0, fontSize: 13 }}>{m.bio}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Credentials */}
			<section className="section section-alt">
				<div className="container">
					<h2 style={{ textAlign: 'center', marginBottom: 24 }}>Our Credentials</h2>
					<div className="grid" style={{ gap: 14 }}>
						{[
							['AZ ROC License', '#XXXXX8142', 'Licensed with the Arizona Registrar of Contractors for residential and commercial remodeling.'],
							['General Liability Insurance', '$2,000,000', 'Full coverage on every job site. Certificate of insurance available upon request.'],
							['Workers\' Compensation', 'Active Policy', 'All field employees and subcontractors are covered under active workers\' comp policies.'],
							['In Business Since', '2019', 'Five-plus years serving Phoenix metro homeowners with consistently high ratings.'],
						].map(([label, val, desc]) => (
							<div key={label} className="card" style={{ padding: '18px 20px' }}>
								<p className="helper" style={{ margin: '0 0 2px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</p>
								<strong style={{ fontSize: 18, color: 'var(--brand)' }}>{val}</strong>
								<p className="helper" style={{ margin: '6px 0 0', fontSize: 13 }}>{desc}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className="cta-section">
				<div className="container">
					<h2>Let's Build Something Great Together</h2>
					<p>Ready to get started? Contact us for a free in-home estimate.</p>
					<Link to="/contact" className="btn" style={{ fontSize: 15, padding: '12px 28px' }}>Get a Free Estimate</Link>
				</div>
			</section>
		</>
	);
}

export default About;
